/**
 * Source-adjust panel setup: section 03 of the controls sidebar.
 *
 * Brightness / Contrast adjustments applied to the loaded source image
 * BEFORE the dither pipeline. Non-destructive: the demo keeps
 * state.sourceOriginal untouched and re-applies these on every slider
 * change. Reset returns all values to their defaults.
 *
 * Scope toggle (Item 11): when a labelmap is active the user can opt-in
 * to applying source-adjust per active region/stack rather than globally.
 *
 * Extracted from createControls (CSS-04): public API of createControls
 * unchanged; no behavioural change.
 */

import { t } from "../../i18n";
import type { SliderVariant } from "../custom-slider";
import type { DemoState, Store } from "../../state";

// ---------------------------------------------------------------------------
// Inline type aliases matching the private interfaces in controls.ts.
// These describe the shape of the helper return values passed via deps.
// ---------------------------------------------------------------------------

interface SectionGroup {
  section: HTMLElement;
  body: HTMLDivElement;
  head: HTMLHeadingElement;
}

interface SliderRow {
  row: HTMLDivElement;
  input: HTMLInputElement;
  valueEl: HTMLSpanElement;
}

interface SliderRowOptions {
  labelKey: string;
  tipKey: string;
  ctrlId: string;
  min: number;
  max: number;
  step: number;
  value: number;
  variant?: SliderVariant;
}

/** Dependencies injected by createControls so this module can use the
 *  private helper functions without duplicating their implementations. */
export interface SourceAdjustDeps {
  store: Store;
  /** The `.controls` aside element; the section is appended here. */
  root: HTMLElement;
  makeSection: (
    ix: string,
    titleKey: string,
    variant?: "" | "violet" | "yellow",
  ) => SectionGroup;
  makeRow: () => HTMLDivElement;
  buildSliderRow: (opts: SliderRowOptions) => SliderRow;
}

// ---------------------------------------------------------------------------
// Setup function
// ---------------------------------------------------------------------------

/**
 * Builds and attaches the SOURCE ADJUST section (section 03) to `deps.root`.
 * Registers its own store subscribers; no return value needed because
 * syncVisibility / syncSliderValues do not reference the source-adjust DOM
 * elements: the section manages its own slider sync internally.
 */
export function setupSourceAdjust(deps: SourceAdjustDeps): void {
  const { store, root, makeSection, makeRow, buildSliderRow } = deps;

  // ========== 03 SOURCE - violet badge per I1 ==========
  // Brightness / Contrast / Gamma adjustments applied to the loaded
  // source BEFORE the dither pipeline. Non-destructive - the demo
  // keeps state.sourceOriginal untouched and re-applies these on
  // every slider change. Reset returns all three to defaults.
  const sourceSection = makeSection("03", "label.source.adjust", "violet");
  root.appendChild(sourceSection.section);

  // Item 11 - scope toggle: apply source adjust to active region only or globally.
  const scopeRow = makeRow();
  const scopeLabel = document.createElement("label");
  scopeLabel.className = "ctrl-checkbox-row";
  scopeLabel.htmlFor = "ctrl-sourceAdjustScope";
  const scopeCheckbox = document.createElement("input");
  scopeCheckbox.type = "checkbox";
  scopeCheckbox.id = "ctrl-sourceAdjustScope";
  scopeCheckbox.checked = store.get().sourceAdjustScope === "region";
  const scopeLabelText = document.createElement("span");
  scopeLabelText.dataset["i18n"] = "sourceAdjust.scopeToggle";
  scopeLabelText.textContent = t("sourceAdjust.scopeToggle");
  scopeLabel.appendChild(scopeCheckbox);
  scopeLabel.appendChild(scopeLabelText);
  scopeRow.appendChild(scopeLabel);
  sourceSection.body.appendChild(scopeRow);

  function syncSourceAdjustScopeRow(state: DemoState): void {
    const hasMask = state.labelMap !== null;
    scopeRow.style.display = hasMask ? "" : "none";
    scopeCheckbox.checked = hasMask && state.sourceAdjustScope === "region";

    if (!hasMask && state.sourceAdjustScope !== "global") {
      store.patch({ sourceAdjustScope: "global" });
    }
  }

  scopeCheckbox.addEventListener("change", () => {
    const hasMask = store.get().labelMap !== null;
    store.patch({
      sourceAdjustScope:
        hasMask && scopeCheckbox.checked ? "region" : "global",
    });
  });

  store.subscribe((state, patch) => {
    if ("labelMap" in patch || "sourceAdjustScope" in patch) {
      syncSourceAdjustScopeRow(state);
    }
  });
  syncSourceAdjustScopeRow(store.get());

  /**
   * Helper - return the effective source-adjust values for the current
   * active area.  Reads per-area override when scope is 'region' and an
   * area is active; falls back to global state otherwise.
   */
  function effectiveSourceAdjust(state: DemoState): {
    brightness: number;
    contrast: number;
  } {
    const areaKey = state.activeStackUuid ?? state.activeRegionUuids[0] ?? null;
    if (state.sourceAdjustScope === "region" && areaKey !== null) {
      return (
        state.regionSourceAdjust[areaKey] ?? { brightness: 0, contrast: 0 }
      );
    }
    return {
      brightness: state.sourceBrightness,
      contrast: state.sourceContrast,
    };
  }

  /**
   * Helper - patch the active-area source-adjust value for a single field.
   * When scope is 'region' and an area is active, writes to the per-area
   * override; otherwise patches the global state field directly.
   */
  function patchSourceAdjustField(
    field: "brightness" | "contrast",
    value: number,
  ): void {
    const st = store.get();
    const areaKey = st.activeStackUuid ?? st.activeRegionUuids[0] ?? null;
    if (st.sourceAdjustScope === "region" && areaKey !== null) {
      // Phase A: write to per-area override rather than global state.
      const prev = st.regionSourceAdjust[areaKey] ?? {
        brightness: 0,
        contrast: 0,
      };
      store.patch({
        regionSourceAdjust: {
          ...st.regionSourceAdjust,
          [areaKey]: { ...prev, [field]: value },
        },
      });
    } else if (field === "brightness") {
      store.patch({ sourceBrightness: value });
    } else {
      store.patch({ sourceContrast: value });
    }
  }

  /**
   * Helper - reset the active-area source-adjust values to zero.
   * Removes the per-area override when scope is 'region', or clears the
   * global brightness/contrast fields when scope is 'global'.
   */
  function resetSourceAdjustArea(): void {
    const st = store.get();
    const areaKey = st.activeStackUuid ?? st.activeRegionUuids[0] ?? null;
    if (st.sourceAdjustScope === "region" && areaKey !== null) {
      // Clear the per-area override by removing the key entirely.
      const next = { ...st.regionSourceAdjust };
      delete next[areaKey];
      store.patch({ regionSourceAdjust: next });
    } else {
      store.patch({ sourceBrightness: 0, sourceContrast: 0 });
    }
  }

  const sourceBrightness = buildSliderRow({
    labelKey: "param.sourceBrightness",
    tipKey: "tip.sourceBrightness",
    ctrlId: "ctrl-sourceBrightness",
    min: -100,
    max: 100,
    step: 1,
    value: store.get().sourceBrightness,
    variant: "violet",
  });
  sourceSection.body.appendChild(sourceBrightness.row);
  sourceBrightness.input.addEventListener("input", () => {
    const v = Number(sourceBrightness.input.value);
    sourceBrightness.valueEl.textContent = String(v);
    patchSourceAdjustField("brightness", v);
  });

  const sourceContrast = buildSliderRow({
    labelKey: "param.sourceContrast",
    tipKey: "tip.sourceContrast",
    ctrlId: "ctrl-sourceContrast",
    min: -100,
    max: 100,
    step: 1,
    value: store.get().sourceContrast,
    variant: "violet",
  });
  sourceSection.body.appendChild(sourceContrast.row);
  sourceContrast.input.addEventListener("input", () => {
    const v = Number(sourceContrast.input.value);
    sourceContrast.valueEl.textContent = String(v);
    patchSourceAdjustField("contrast", v);
  });

  const sourceResetRow = makeRow();
  const sourceResetButton = document.createElement("button");
  sourceResetButton.type = "button";
  sourceResetButton.className = "btn btn-ghost source-reset";
  sourceResetButton.dataset["i18n"] = "action.source.reset";
  sourceResetButton.addEventListener("click", () => {
    sourceBrightness.input.value = "0";
    sourceBrightness.valueEl.textContent = "0";
    sourceContrast.input.value = "0";
    sourceContrast.valueEl.textContent = "0";
    resetSourceAdjustArea();
  });
  sourceResetRow.appendChild(sourceResetButton);
  sourceSection.body.appendChild(sourceResetRow);

  // Sync source-adjust sliders when active region / stack / scope changes.
  store.subscribe((state, patch) => {
    if (
      "activeRegionUuids" in patch ||
      "activeStackUuid" in patch ||
      "sourceAdjustScope" in patch ||
      "regionSourceAdjust" in patch ||
      "sourceBrightness" in patch ||
      "sourceContrast" in patch
    ) {
      const { brightness, contrast } = effectiveSourceAdjust(state);
      sourceBrightness.input.value = String(brightness);
      sourceBrightness.valueEl.textContent = String(brightness);
      sourceContrast.input.value = String(contrast);
      sourceContrast.valueEl.textContent = String(contrast);
    }
  });
}
